// src/rms/components/PinModal.jsx
// ============================================================================
// The PIN prompt
// ============================================================================
// One component for both jobs, because to the person at the counter they are
// the same gesture: four digits and the box goes away.
//
//   SIGNING    "who is handing this vehicle over" -- returns a signature id
//              that gets written onto the row and validated by a trigger.
//   UNLOCKING  "who is asking to see the figures" -- returns the figures,
//              fetched from the server on the strength of the PIN.
//
// It submits on the fourth digit rather than on a button. That is not a
// flourish: a counter has a customer standing at it, and Enter-after-typing is
// one more thing to explain to somebody who was hired last week.
//
// The two jobs are told apart by what the caller does with the value, not by a
// prop: this component collects four digits and hands them over. Signing and
// unlocking differ in what happens next, not in what the person is asked for.
//
// If they have no PIN yet, this becomes "choose one" instead of "enter one" --
// which is where the owner ends up setting theirs, the first time the business
// asks for a signature.
// ============================================================================

import React, { useEffect, useRef, useState } from "react";
import { useRms } from "../context/RmsContext";
import pinService from "../data/pinService";
import { Modal, Banner } from "./ui";

const LENGTH = 4;

export default function PinModal({ forceSetup = false, title, explain, setupExplain, onCancel, onResolved }) {
  const { operator, member, can, t } = useRms();

  const [digits, setDigits] = useState("");
  const [confirming, setConfirming] = useState(null);
  const [needsSetup, setNeedsSetup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // forceSetup is the Settings path: "change my PIN" has to reach the choose-
    // and-confirm flow even though one already exists. Everywhere else the
    // component works it out -- somebody signing a checkout should not have to
    // know whether they have ever set one.
    if (forceSetup) {
      setNeedsSetup(true);
      return;
    }
    pinService.hasPin(operator, member).then((r) => setNeedsSetup(!r.data));
  }, [operator, member, forceSetup]);

  useEffect(() => {
    // Focus lands on the field, so the first keystroke counts. On a touch
    // screen this also raises the keypad without a second tap.
    inputRef.current?.focus();
  }, [needsSetup, confirming]);

  const submit = async (value) => {
    setBusy(true);
    setProblem(null);

    if (needsSetup) {
      if (!confirming) {
        // Typed once, now type it again. A PIN chosen with a slipped finger
        // and never confirmed locks somebody out of their own signature.
        const problemText = pinService.validatePin(value);
        setBusy(false);
        if (problemText) {
          setProblem(problemText);
          setDigits("");
          return;
        }
        setConfirming(value);
        setDigits("");
        return;
      }
      if (value !== confirming) {
        setBusy(false);
        setProblem(t("pin.mismatch", "Those did not match. Start again."));
        setConfirming(null);
        setDigits("");
        return;
      }
      const set = await pinService.setPin(operator, member, value);
      setBusy(false);
      if (!set.success) {
        setProblem(set.error);
        setConfirming(null);
        setDigits("");
        return;
      }
      setConfirming(null);
      setDigits("");
      setProblem(null);

      // Two different endings, because there are two reasons to be here.
      //
      // From Settings there is nothing to sign -- setting it WAS the task, so
      // the box closes. From a checkout there is: they now enter the PIN they
      // just chose, for the action they were trying to take, which has the
      // useful side effect of proving they can remember it a minute later.
      if (forceSetup) {
        await onResolved(value);
        return;
      }
      setNeedsSetup(false);
      return;
    }

    const result = await onResolved(value);
    setBusy(false);
    if (result && result.error) {
      setProblem(result.error);
      setDigits("");
    }
  };

  /**
   * The way back for somebody who has forgotten theirs.
   *
   * Owner only, because that is what the database allows -- rms_clear_member_pin
   * is gated on owner and nobody else can clear even their own. For everybody
   * else the answer is a sentence, not a button: ask the owner, who can already
   * clear it for them from the roster.
   *
   * Without this an owner who forgot their PIN was stranded outright. Every
   * screen that could have helped is behind the thing they forgot.
   */
  const forget = async () => {
    setBusy(true);
    const result = await pinService.clearMyPin(operator, member);
    setBusy(false);
    if (!result.success) {
      setProblem(result.error);
      return;
    }
    // Straight into choosing a new one, rather than closing and making them
    // find their way back to whatever they were trying to do.
    setDigits("");
    setConfirming(null);
    setProblem(null);
    setNeedsSetup(true);
  };

  const onChange = (raw) => {
    const value = raw.replace(/\D/g, "").slice(0, LENGTH);
    setDigits(value);
    if (value.length === LENGTH) submit(value);
  };

  const heading = needsSetup
    ? confirming
      ? t("pin.confirmTitle", "Type it once more")
      : t("pin.setTitle", "Choose your PIN")
    : title || t("pin.title", "Staff PIN");

  // setupExplain, not explain. A caller's `explain` describes the thing being
  // SIGNED -- "your PIN goes on the record beside it" -- and reading that while
  // being asked to choose a PIN for the first time is a non-sequitur. A caller
  // with something to say about the choosing itself passes setupExplain.
  const help = needsSetup
    ? confirming
      ? t("pin.confirmHelp", "So we know it was not a slip.")
      : setupExplain ||
        t("pin.setHelp", "Four digits. It signs your actions so the log shows who did what — it does not change what you can reach.")
    : explain || t("pin.help", "Enter your PIN to sign this.");

  // onCancel omitted means there is no way out, and the box says so by having
  // no X and no Cancel. Used by RequirePin: somebody who dismissed that would
  // be signed in and unable to take a booking, which is worse than the box.
  return (
    <Modal
      title={heading}
      onClose={onCancel || undefined}
      width={380}
      footer={
        onCancel ? (
          <button type="button" className="rms-btn rms-btn--ghost" onClick={onCancel}>
            {t("action.cancel", "Cancel")}
          </button>
        ) : null
      }
    >
      {problem ? <Banner tone="danger">{problem}</Banner> : null}

      <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", marginTop: 0 }}>{help}</p>

      <input
        ref={inputRef}
        className="rms-pin-input"
        // Numeric, but text: type="number" brings spinner arrows, allows a
        // minus sign and an exponent, and drops leading zeros -- all wrong for
        // something that is four characters rather than a quantity.
        type="password"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={LENGTH}
        value={digits}
        disabled={busy}
        onChange={(e) => onChange(e.target.value)}
        aria-label={heading}
      />

      <div className="rms-pin-dots" aria-hidden="true">
        {Array.from({ length: LENGTH }, (_, i) => (
          <span key={i} className={`rms-pin-dot${i < digits.length ? " is-filled" : ""}`} />
        ))}
      </div>

      {/* Only when being asked to ENTER one. During setup there is nothing to
          have forgotten yet. */}
      {!needsSetup ? (
        <p className="rms-help" style={{ textAlign: "center", marginTop: 12, marginBottom: 0 }}>
          {can?.manageStaff ? (
            <button type="button" className="rms-linkish rms-linkish--tap" onClick={forget} disabled={busy}>
              {t("pin.forgot", "I've forgotten my PIN")}
            </button>
          ) : (
            t("pin.forgotAskOwner", "Forgotten it? The owner can clear it, and you choose a new one.")
          )}
        </p>
      ) : null}
    </Modal>
  );
}
